import { set } from '@ember/object';
import FilterConstants from '../../filter-constants';
import FirstOrderStage from '../stages/first-order';
import SecondOrderStage from '../stages/second-order';
import * as ArrayUtils from './array';

function gainNeed1(targetGain, stages, centerFrequency, maxQ) {
  var result = stages.map((stage) => {
    if (stage instanceof FirstOrderStage) {
      var gainNeed = gainNeeded(targetGain, stages, centerFrequency);
      var gain = stage.gain;
      if (gainNeed.gainNeeded) {
        var maxGainAllowed = 10;
        if (maxQ > 1) {
          maxGainAllowed = maxQ * 10;
        }

        gain = Math.min(maxGainAllowed, gainNeed.extraGainNeeded);
      }

      set(stage, 'gain', gain);
    }

    return stage;
  });

  return result;
}

function gainNeed2(targetGain, stages, centerFrequency, maxQ) {
  var gainNeed = gainNeeded(targetGain, stages, centerFrequency);
  var result = stages;
  if (gainNeed.gainNeeded) {
    var sortStages = stages;
    sortStages.sort(function (obj1, obj2) {
      return obj1.StageQ > obj2.StageQ ? -1 : obj1.StageQ < obj2.StageQ ? 1 : 0;
    });

    var value = [];
    for (var i = 0; i < sortStages.length; i++) {
      if (sortStages[i].stageQ < 0.708) {
        value[i] = sortStages[i];
        break;
      }
    }

    var index = sortStages.indexOf(value[i]) - 1;
    sortStages = ArrayUtils.rotate(sortStages, -index); // object rotation based on the value of Q

    result = sortStages.map((stage) => {
      if (stage.order === 2) {
        var qMargin = maxQ / stage.stageQ;
        var gainNeed2 = gainNeeded(targetGain, stages, centerFrequency);
        if (gainNeed2.gainNeeded) {
          var minVal = Math.min(gainNeed2.extraGainNeeded, qMargin);
          var stageGain = stage.gain;
          set(stage, 'gain', stageGain * minVal);
        }
      }

      return stage;
    });
  }

  return result;
}

function gainNeed3(targetGain, stages, centerFrequency, dataQ) {
  var gainNeed3 = gainNeeded(targetGain, stages, centerFrequency);
  var result = stages;
  if (gainNeed3.gainNeeded) {
    var oneByNumOfStages = 1 / dataQ.length;
    var multiplier = Math.pow(gainNeed3.extraGainNeeded, oneByNumOfStages);

    result = stages.map((stage) => {
      var stageGain = stage.gain;
      set(stage, 'gain', stageGain * multiplier);
      return stage;
    });
  }

  return result;
}

/**
Distribute Gain Algorithm
LAST UPDATED : 09/19/2012 11:11 AM
@author Created by : Archita Ghosh on behalf of Collaborative Consulting and ADI
@function gainNeeded
@param {int} targetGain  target gain provided by the user
@param {object} order  order obtained after the bessel algorithm.
@returns gainNeeded a boolean value indicating if gain is needed or not ,extraGainNeeded,double value indicating how much extra gain is needed.
@description <p> This function takes input as target gain and stage specifications and calculates extra gain needed</p>
<p><b>INPUT : target gain, order , stage specifications of the order</p></b>
<p><b>OUTPUT : Gain Needed   -Boolean
Extra Gain still needed  -Double</p></b>
<p><b>NOTE</b> : We do not need center frequency in case of low pass filter </p>
*/

function gainNeeded(targetGain, stages, centerFrequency) {
  var allgain = 1;
  stages.forEach((stage) => {
    var multiplier = 1;
    if (stage.stageType === FilterConstants.filterType.bandPass) {
      let factor = centerFrequency / stage.stageFp - stage.stageFp / centerFrequency;
      multiplier = 1 / Math.sqrt(1 + stage.stageQ * stage.stageQ * factor * factor);
    }

    allgain = allgain * stage.gain * multiplier;
  });

  var gainNeeded = false;
  var extraGainNeeded = 1;

  if (targetGain > allgain) {
    gainNeeded = true;
    extraGainNeeded = targetGain / allgain;
  }

  var result = {
    gainNeeded: gainNeeded,
    extraGainNeeded: extraGainNeeded,
  };

  return result;
}

export function setPreviousAndNextStage(stages) {
  var result = stages.map((stage, index) => {
    if (index > 0) {
      set(stage, 'previousStage', stages[index - 1]);
    }

    if (index < stages.length - 1) {
      set(stage, 'nextStage', stages[index + 1]);
    }

    return stage;
  });

  return result;
}

//function to add stageLetter to each stage of Order object
export function addStageLetter(stages) {
  // get the stage letter array to add the stage letter to the order object
  var stageLetterArray = FilterConstants.stageLetter;
  //var orderObject = data;

  for (var i = 0; i < stages.length; i++) {
    var stage = stages[i];
    stage.stageLetter = stageLetterArray[i]; // adding the stage letter to each order object
  }

  return stages;
}

/**
 * @param stageValues array of stage values (stageFp, stageQ, stageFz, gain, orderType, stageId)
 */
export function createTargetStages(stageValues) {
  var result = stageValues.map((stageValue) => {
    var stage = null;
    if (stageValue.orderType === 1) {
      stage = FirstOrderStage.create({
        stageFp: stageValue.stageFp,
        stageQ: stageValue.stageQ,
        gain: stageValue.gain,
        stageFz: stageValue.stageFz,
        stageId: stageValue.stageId,
      });
    } else {
      stage = SecondOrderStage.create({
        stageFp: stageValue.stageFp,
        stageQ: stageValue.stageQ,
        gain: stageValue.gain,
        stageFz: stageValue.stageFz,
        stageId: stageValue.stageId,
      });
    }

    return stage;
  });

  return result;
}

export function clone(stage) {
  var result = null;
  if (stage instanceof FirstOrderStage) {
    result = FirstOrderStage.create(stage);
  } else {
    result = SecondOrderStage.create(stage);
  }

  return result;
}

export function distributeGains(targetGain, stages, centerFrequency) {
  var dataQ = stages.map((stage) => {
    return stage.stageQ;
  });

  var stagesClone = [];
  for (var i = 0; i < stages.length; i++) {
    var item = clone(stages[i]);
    stagesClone.push(item);
  }

  var maxQ = Math.max.apply(Math, dataQ);
  stagesClone = gainNeed1(targetGain, stagesClone, centerFrequency, maxQ);
  stagesClone = gainNeed2(targetGain, stagesClone, centerFrequency, maxQ);
  stagesClone = gainNeed3(targetGain, stagesClone, centerFrequency, dataQ);

  // sorting the stage array in ascending order according to the value of StageId
  stagesClone.sort(function (obj1, obj2) {
    return obj1.stageId < obj2.stageId ? -1 : obj1.stageId > obj2.stageId ? 1 : 0;
  });

  return stagesClone;
}

export function getStageInputsInImplementationOrder(stageInputs, detailsOfCircuits) {
  let individualStageInputs = [];
  for (let i = 0; i < detailsOfCircuits.length; i++) {
    const item = stageInputs.find((stage) => stage.stageLetter === detailsOfCircuits[i].stageLetter);
    if (item) {
      individualStageInputs.push(item);
    }
  }
  return individualStageInputs;
}
